import React, { useEffect, useMemo, useState } from 'react'
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Button,
  TableBody,
  Typography
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import TrackingBody from './TableBody'
import { formatPesos } from '../../utils/format'
import {
  clearCommissionsMessage,
  CommissionDraft,
  CommissionInvoiceLine,
  Invoice,
  commissionsLoader,
  getCommissions,
  getCommissionContextSuccess,
  postInvoice,
  selectCommissions,
  selectCommissionTotals,
  selectCommissionDraftContext,
  selectDeductions
} from './state/commissionsSlice'
import { createPdf } from '../../utils/buildInvoice'
import { userProfile } from '@views/profile/state/profileSlice'
import UploadInvoiceModal from './UploadInvoiceModal'
import { processCommission } from './util/processCommissions'
import {
  acknowledgeCommissionDraftService,
  replaceCommissionDraftService,
  reserveInvoiceDraftService
} from '@http/commissionsService'
import { selectCredentials } from '@views/loginView/state/loginSlice'
import { hasCompletePaymentProfile } from '../../utils/paymentProfile'
import DraftContextPanel from './DraftContextPanel'

export type Order = 'asc' | 'desc'

const currencyFormat = (value: number) => `$ ${formatPesos(Math.round(value || 0))}`

const buildDraftPdfRows = (lines: CommissionInvoiceLine[]) =>
  lines.map(line => {
    if (line.isDeduction) {
      return [line.name, '', '', '', '', '', '', `- ${currencyFormat(line.amount || 0)}`]
    }
    const prepaymentAmount = Number(line.prepaymentAmount) || 0
    const total = (Number(line.amount) || 0) + (Number(line.promotionAmount) || 0) - prepaymentAmount
    return [
      line.name,
      '',
      `${line.percentage || 0}`,
      `${line.promotionPercentage || 0}`,
      prepaymentAmount ? `- ${currencyFormat(prepaymentAmount)}` : '-',
      line.amount ? currencyFormat(line.amount) : '-',
      line.promotionAmount ? currencyFormat(line.promotionAmount) : '-',
      currencyFormat(total)
    ]
  })

const CommissionsReferrals: React.FC = () => {
  const [page, setPage] = useState(0)
  const [isModalOpen, setModalOpen] = useState(false)
  const [reservedInvoice, setReservedInvoice] = useState<CommissionDraft | null>(null)
  const [draftActionBusy, setDraftActionBusy] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const rows = useSelector(selectCommissions)
  const deductions = useSelector(selectDeductions)
  const totals = useSelector(selectCommissionTotals)
  const draftContext = useSelector(selectCommissionDraftContext)
  const { isLoading, isFailed, message } = useSelector(commissionsLoader)
  const { name, document, phone, accountData } = useSelector(userProfile)
  const { cocap } = useSelector(selectCredentials)

  useEffect(() => {
    dispatch(getCommissions())
    setPage(0)
    setRowsPerPage(10)
  }, [dispatch])

  useEffect(() => {
    setReservedInvoice(draftContext?.activeDraft || null)
  }, [draftContext])

  useEffect(() => {
    if (isFailed && message) {
      enqueueSnackbar(message, { variant: 'error' })
      dispatch(clearCommissionsMessage())
    }
  }, [dispatch, enqueueSnackbar, isFailed, message])

  const totalGross = useMemo(() => formatPesos(Math.round(totals?.gross || 0)), [totals?.gross])
  const totalDeductions = useMemo(() => formatPesos(Math.round(totals?.deductions || 0)), [totals?.deductions])
  const totalNet = useMemo(() => formatPesos(Math.round(totals?.net || 0)), [totals?.net])
  const hasPositiveGross = (totals?.gross || 0) > 0 && rows.length > 0
  const hasPaymentProfile = hasCompletePaymentProfile(accountData)

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const buildInvoicePayload = (file?: string): Invoice => ({
    commissions: rows.map(item => {
      const { commission, prepayment } = processCommission(item)
      return {
        personId: item._id || '',
        name: item.name,
        ...(commission.percentage !== undefined && {
          percentage: commission.percentage
        }),
        ...(commission.amount !== undefined && {
          amount: commission.amount
        }),
        ...(prepayment > 0 && {
          prepaymentAmount: prepayment
        }),
        ...(commission.promotionAmount !== undefined && {
          promotionAmount: commission.promotionAmount
        }),
        ...(commission.promotionPercentage !== undefined && {
          promotionPercentage: commission.promotionPercentage
        })
      }
    }),
    deductions: deductions.map(item => ({
      personId: item.personId,
      amount: item.deductionAmount,
      name: item.name
    })),
    amount: totals?.net || 0,
    ...(file && { file }),
    type: 'commission'
  })

  const renderDraftPdf = (draft: CommissionDraft) => {
    const total = Math.round(draft.amount || 0)
    createPdf(
      `cta-cobro-${document}-${new Date().toISOString().split('T')[0]}`,
      draft.serial,
      name,
      document,
      total,
      formatPesos(total),
      buildDraftPdfRows(draft.commissions),
      accountData?.bankName,
      accountData?.accountType,
      accountData?.accountNumber,
      accountData?.accountDocument,
      accountData?.physicalAddress,
      phone
    )
    setReservedInvoice(draft)
  }

  const downloadPdf = async () => {
    try {
      if (draftContext?.activeDraft) {
        renderDraftPdf(draftContext.activeDraft)
        return
      }

      const draft = await reserveInvoiceDraftService(cocap || '', buildInvoicePayload())
      renderDraftPdf(draft)
      dispatch(getCommissions())
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message || 'No fue posible reservar la cuenta de cobro para descargar el PDF.'
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  const keepCurrentDraft = async () => {
    if (!draftContext?.activeDraft) return
    setDraftActionBusy(true)
    try {
      const context = await acknowledgeCommissionDraftService(
        cocap || '',
        draftContext.activeDraft._id,
        draftContext.comparison.fingerprint
      )
      dispatch(getCommissionContextSuccess(context))
      enqueueSnackbar('Conservaremos este borrador durante el día de hoy.', {
        variant: 'success'
      })
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.message ||
        'No fue posible conservar el borrador. La información se actualizará.'
      enqueueSnackbar(errorMessage, { variant: 'error' })
      dispatch(getCommissions())
    } finally {
      setDraftActionBusy(false)
    }
  }

  const replaceCurrentDraft = async () => {
    if (!draftContext?.activeDraft) return
    setDraftActionBusy(true)
    try {
      const context = await replaceCommissionDraftService(
        cocap || '',
        draftContext.activeDraft._id,
        draftContext.comparison.fingerprint
      )
      dispatch(getCommissionContextSuccess(context))
      setReservedInvoice(context.activeDraft)
      enqueueSnackbar('La cuenta de cobro fue actualizada con un nuevo consecutivo.', { variant: 'success' })
      dispatch(getCommissions())
    } catch (error) {
      const responseContext = (error as any)?.response?.data?.context
      if (responseContext) dispatch(getCommissionContextSuccess(responseContext))
      enqueueSnackbar((error as any)?.response?.data?.message || 'No fue posible actualizar la cuenta de cobro.', {
        variant: 'error'
      })
      dispatch(getCommissions())
    } finally {
      setDraftActionBusy(false)
    }
  }

  const onLoadInvoice = async (file: string) => {
    if (!reservedInvoice?._id) {
      enqueueSnackbar('Debes descargar la cuenta de cobro antes de subirla.', {
        variant: 'error'
      })
      return
    }

    setModalOpen(false)
    dispatch(
      postInvoice({
        ...buildInvoicePayload(file),
        invoiceId: reservedInvoice._id
      })
    )
  }

  const disabledInvoice = !hasPaymentProfile || !hasPositiveGross
  const invalidDraft = draftContext?.comparison.status === 'invalidating_changes'
  const decisionRequired =
    draftContext?.comparison.status === 'additions_only' && !draftContext?.acknowledgement?.validForToday

  return (
    <>
      <div className="center_view padding_view" id="commissions-view">
        {draftContext && (
          <DraftContextPanel
            context={draftContext}
            busy={draftActionBusy}
            onKeep={keepCurrentDraft}
            onReplace={replaceCurrentDraft}
          />
        )}
        <TableContainer className="container-table" component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell className="table_header" style={{ color: 'white' }} align="left">
                  REFERIDO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  OFICINA
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  EXITOSO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  FECHA PRIMER PAGO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  HONORARIOS SIN IVA
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  TOTAL ABONADO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  PORCENTAJE COMISIÓN
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  PORCENTAJE POR PATROCINIO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  PAGADO ANTICIPO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  COMISIÓN
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  COMISIÓN POR PATROCINIO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  TOTAL COMISIÓN
                </TableCell>
              </TableRow>
            </TableHead>
            <TrackingBody users={rows} rowsPerPage={rowsPerPage} page={page} isLoading={isLoading} isTeam={true} />
          </Table>
          <div className="pagination-row">
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <div>Total comisiones: ${totalGross}</div>
          </div>
        </TableContainer>
        {!hasPositiveGross && !isLoading && (
          <Box marginTop="1rem" width="100%">
            <Typography>No tienes comisiones pendientes por cobrar.</Typography>
          </Box>
        )}
        {deductions.length > 0 && (
          <>
            <br />
            <TableContainer className="container-table" component={Paper}>
              <Table aria-label="deductions table">
                <TableHead>
                  <TableRow>
                    <TableCell className="table_header" style={{ color: 'white' }} align="left">
                      REFERIDO
                    </TableCell>
                    <TableCell className="table_header" style={{ color: 'white' }} align="center">
                      OFICINA
                    </TableCell>
                    <TableCell className="table_header" style={{ color: 'white' }} align="center">
                      RECHAZO
                    </TableCell>
                    <TableCell className="table_header" style={{ color: 'white' }} align="center">
                      HONORARIOS SIN IVA
                    </TableCell>
                    <TableCell className="table_header" style={{ color: 'white' }} align="center">
                      PAGADO ANTICIPO
                    </TableCell>
                    <TableCell className="table_header" style={{ color: 'white' }} align="center">
                      TOTAL
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deductions.map(item => (
                    <TableRow key={item.personId}>
                      <TableCell align="left">{item.name}</TableCell>
                      <TableCell align="center">{item.stages?.office || '-'}</TableCell>
                      <TableCell align="center">{item.stages?.rejected || 'rechazado'}</TableCell>
                      <TableCell align="center">
                        {item.stages?.fee ? currencyFormat(item.stages.fee / 1.19) : '-'}
                      </TableCell>
                      <TableCell align="center">{currencyFormat(item.prepayment?.amount || 0)}</TableCell>
                      <TableCell align="center">- {currencyFormat(item.deductionAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end" padding="1rem" flexDirection="column" alignItems="flex-end">
                <Typography>Total comisiones: ${totalGross}</Typography>
                <Typography>Total deducciones: - ${totalDeductions}</Typography>
                <Typography>Total neto: ${totalNet}</Typography>
              </Box>
            </TableContainer>
          </>
        )}
        {!deductions.length && hasPositiveGross && (
          <Box display="flex" justifyContent="flex-end" width="100%" marginTop="1rem">
            <Typography>Total neto: ${totalNet}</Typography>
          </Box>
        )}
        <br />
        <Box width="100%" justifyContent="right" display="flex" marginBottom="1rem">
          <span>
            {hasPaymentProfile
              ? 'Tu perfil de pago está completo para solicitar comisiones.'
              : 'Completa tu perfil con datos bancarios, dirección física, link de cédula y link de RUT en '}
            {!hasPaymentProfile && <a href="/profile">Datos bancarios para pago de comisiones</a>}
          </span>
        </Box>
        <Box display="flex" justifyContent="right" width="100%">
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModalOpen(true)}
            disabled={disabledInvoice || !reservedInvoice || invalidDraft || decisionRequired}
            style={{ marginRight: '10px' }}
          >
            Subir cuenta de cobro
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadPdf}
            disabled={disabledInvoice || Boolean(invalidDraft) || Boolean(decisionRequired)}
          >
            {reservedInvoice ? 'Volver a descargar' : 'Descargar cuenta de cobro'}
          </Button>
        </Box>
        <UploadInvoiceModal open={isModalOpen} onAccept={onLoadInvoice} onReject={() => setModalOpen(false)} />
      </div>
    </>
  )
}

export default CommissionsReferrals
